import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { servicePath } from "../lib/paths";

const SERVICIOS = [
  {
    id: "spelling",
    de: {
      t: "Rechtschreibung & Orthotypografie",
      d: "Rechtschreibung, Zeichensetzung und die typografischen Feinheiten, die ein Buch vom Manuskript unterscheiden.",
    },
    es: {
      t: "Ortografía y ortotipografía",
      d: "Ortografía, puntuación y las finuras tipográficas que separan un libro de un manuscrito.",
    },
  },
  {
    id: "style",
    de: {
      t: "Stil & narrativer Ton",
      d: "Rhythmus, Register, Dialoge. Ich schärfe Ihre Stimme, ohne sie durch meine zu ersetzen.",
    },
    es: {
      t: "Estilo y tono narrativo",
      d: "Ritmo, registro, diálogos. Afilo tu voz sin sustituirla por la mía.",
    },
  },
  {
    id: "layout",
    de: {
      t: "Satz & Gestaltung",
      d: "Innensatz und Umschlag, druckfertig: Typografie, Satzspiegel, Kolumnentitel, Druckvorstufe.",
    },
    es: {
      t: "Maquetación y diseño",
      d: "Interior y cubierta listos para imprenta: tipografía, caja, folios y preimpresión.",
    },
  },
  {
    id: "analysis",
    de: {
      t: "Literarische Analyse",
      d: "Ein zweiter Blick auf Struktur, Figuren und Kontinuität, bevor der Text festgeschrieben wird.",
    },
    es: {
      t: "Análisis literario",
      d: "Una segunda mirada a estructura, personajes y continuidad antes de fijar el texto.",
    },
  },
];

const T = {
  de: {
    rotulo: "Werkstatt",
    titulo: "Dieselbe Sorgfalt, für fremde Manuskripte.",
    entrada:
      "Was in meine eigenen Bücher geht, biete ich auch anderen an. Ich arbeite auf Spanisch und auf Deutsch, an wenigen Texten gleichzeitig, und lege jede Änderung schriftlich vor, bevor sie angewendet wird.",
    aclaracionRotulo: "Damit es keine Missverständnisse gibt",
    aclaracion:
      "An Ihrem Buch arbeite ich sehr wohl: Ich lektoriere, setze, gestalte und übersetze es. Genau das ist meine Arbeit. Was ich nicht tue, ist es zu verlegen: Der Blauer Wolf Verlag bringt ausschließlich meine eigenen Bücher heraus. Ich nehme also keine Manuskripte an, um sie unter meinem Namen zu veröffentlichen, vergebe meine ISBN nicht und trete nicht als Ihr Verlag auf. Ich mache Ihr Buch fertig — veröffentlichen tun Sie, wo Sie möchten: im Selbstverlag, bei einem Verlag Ihrer Wahl oder über eine Vertriebsplattform. Die Rechte an Ihrem Text bleiben zu jedem Zeitpunkt bei Ihnen.",
    mas: "Mehr dazu",
    trad: {
      rotulo: "Übersetzung",
      t: "Zwischen zwei Sprachen, nicht zwischen zwei Wörterbüchern.",
      d: "Übersetzung Spanisch ↔ Deutsch von jemandem, der in beiden Sprachen schreibt und in beiden Kulturen lebt. Literarisch übersetzt, nicht wörtlich übertragen.",
    },
    cta: "Schreiben Sie mir über Ihren Text",
    ctaNota:
      "Erzählen Sie mir kurz, worum es geht und in welchem Zustand der Text ist. Sie bekommen eine ehrliche Einschätzung und einen Preis — kein Angebotsformular, sondern eine Antwort. Und keine Veröffentlichung: die bleibt Ihre Entscheidung.",
  },
  es: {
    rotulo: "Taller",
    titulo: "El mismo cuidado, para manuscritos ajenos.",
    entrada:
      "Lo que pongo en mis libros se lo ofrezco también a otros. Trabajo en español y en alemán, con pocos textos a la vez, y te entrego cada cambio por escrito antes de aplicarlo.",
    aclaracionRotulo: "Para que no haya malentendidos",
    aclaracion:
      "Sí trabajo tu libro: lo corrijo, lo maqueto, lo diseño y lo traduzco. Eso es justamente lo que hago. Lo que no hago es publicarlo: Blauer Wolf Verlag saca al mercado únicamente mis propios libros, así que no recibo manuscritos para editarlos bajo el sello, no asigno mi ISBN y no figuro como tu editorial. Te dejo el libro listo y publicas tú, donde quieras: por tu cuenta, con la editorial que elijas o a través de una plataforma de distribución. Los derechos sobre tu texto siguen siendo tuyos en todo momento.",
    mas: "Ver detalle",
    trad: {
      rotulo: "Traducción",
      t: "Entre dos lenguas, no entre dos diccionarios.",
      d: "Traducción español ↔ alemán de quien escribe en las dos y vive en las dos culturas. Traducción literaria, no traslación literal.",
    },
    cta: "Escríbeme sobre tu texto",
    ctaNota:
      "Cuéntame de qué se trata y en qué estado está el texto. Recibirás una valoración honesta y un precio — no un formulario de presupuesto, sino una respuesta. Publicar, eso lo decides y lo haces tú.",
  },
};

export default function Services({ language = "de" }) {
  const t = T[language];

  return (
    <div className="bg-papel text-tinta">
      <Helmet>
        <title>{`${t.rotulo} – Blauer Wolf Verlag`}</title>
        <meta name="description" content={t.entrada.slice(0, 155)} />
      </Helmet>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-16"
        >
          <p className="rotulo text-loboOscuro mb-5">{t.rotulo}</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight tracking-titulo mb-7">
            {t.titulo}
          </h1>
          <p className="text-lg text-tinta/80">{t.entrada}</p>
        </motion.div>

        {/* El sello publica solo a su autor. Conviene decirlo antes que nada. */}
        <motion.aside
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mb-16 border-l-2 border-lobo bg-hueso/60 px-6 py-6 sm:px-8 sm:py-7 max-w-3xl"
        >
          <h2 className="rotulo text-loboOscuro mb-3">{t.aclaracionRotulo}</h2>
          <p className="text-tinta/85 leading-relaxed">{t.aclaracion}</p>
        </motion.aside>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-tinta/15 border border-tinta/15">
          {SERVICIOS.map((s, i) => {
            const c = s[language];
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="bg-papel"
              >
                <Link
                  to={servicePath(language, s.id)}
                  className="group flex h-full flex-col p-8 sm:p-10 hover:bg-hueso transition-colors"
                >
                  <span className="rotulo text-sombra mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-serif text-2xl mb-3 leading-snug">{c.t}</h2>
                  <p className="text-tinta/75 mb-6 flex-1">{c.d}</p>
                  <span className="rotulo text-loboOscuro group-hover:underline underline-offset-4">
                    {t.mas} →
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mt-20 pt-12 border-t border-tinta/15 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-20"
        >
          <div>
            <p className="rotulo text-loboOscuro mb-5">{t.trad.rotulo}</p>
            <h2 className="font-serif text-2xl sm:text-3xl leading-snug mb-5">
              {t.trad.t}
            </h2>
            <p className="text-tinta/80 max-w-prosa">{t.trad.d}</p>
          </div>

          <div className="lg:border-l lg:border-tinta/15 lg:pl-20 flex flex-col justify-center">
            <p className="text-tinta/80 max-w-prosa mb-7">{t.ctaNota}</p>
            <Link
              to={`/${language}#kontakt`}
              className="rotulo self-start bg-tinta text-hueso px-7 py-3.5 hover:bg-loboOscuro transition-colors"
            >
              {t.cta}
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
